// src/components/ProBadge.tsx
import { useTranslation } from "react-i18next";
import React from "react";
import clsx from "clsx";
import { A, pillTone } from "../styles/appleClasses";

type ProBadgeProps = {
  /** Hide/show without conditionals at call sites */
  show?: boolean;
  /** "PRO" by default, but flexible (e.g., "ULTRA") */
  label?: string;
  /** Positions badge in the top-right corner of a relative parent */
  overlay?: boolean;
  /** xs matches your snippet; bump if you need larger */
  size?: "xs" | "sm" | "md";
  /** Extra utility classes for fine-tuning placement */
  className?: string;
};

const SIZE: Record<NonNullable<ProBadgeProps["size"]>, string> = {
  // Keep the pill *style* locked; size only tweaks text/padding.
  xs: "text-[10px] px-2 py-0.5",
  sm: "text-xs px-2.5 py-0.5",
  md: "text-[13px] px-2.5 py-0.5",
};

export default function ProBadge({
  show = true,
  label = "PRO",
  overlay = false,
  size = "xs",
  className = "",
}: ProBadgeProps) {
  const { t } = useTranslation();
  if (!show) return null;

  const wrapper = overlay ? "absolute top-3 right-3" : "";

  return (
    <div className={clsx(wrapper, className)}>
      <span
        className={clsx(
          A.pillBase,          // ✅ locked pill base (ring-1, rounded-full, etc.)
          pillTone.brand,      // ✅ appleblue tint + ring
          SIZE[size]           // size override (kept for compact header use)
        )}
        role="status"
        aria-label={t("shared.proBadge.account", { defaultValue: "Pro account" })}
        title={t("shared.proBadge.account", { defaultValue: "Pro account" })}
      >
        {label}
      </span>
    </div>
  );
}
